import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import React, { ChangeEvent, useState, type JSX } from 'react';
import { ERROR_MESSAGE } from '../../../components/common/Alert';
import { CustomDatePicker } from '../../../components/common/customDatePickers';
import Formular from '../../../components/common/Formular';
import PersonAnzeige from '../../../components/common/PersonAnzeige';
import SearchPersonUnternehmen from '../../../components/common/SearchPersonUnternehmen';
import { SPACING_BETWEEN_COLUMNS, SPACING_BETWEEN_FORM_FIELDS } from '../../../components/common/spacings';
import WarningSign from '../../../components/common/WarningSign';
import { PersonEingabeMaske } from '../../../components/PersonEingabeMaske/PersonEingabeMaske';
import { YesNoFormControl } from '../../../components/YesNoFormControl';
import { aktionErlaubt } from '../../../domain/aktionErlaubt';
import { aktualisierePersonMitKundennummer } from '../../../domain/aktualisierePerson';
import { isAenderbar } from '../../../domain/isAenderbar';
import { AKTION_KUNDENDATEN_ERFASSEN } from '../../../frontendConstants';
import { useUser } from '../../../hooks/useUser';
import { PersonenTyp } from '../../../shared/constants';
import { getMessageFromError } from '../../../shared/throw';
import { FahrzeugFinanzierung, Person, Vorgang } from '../../../types';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

const FAHRZEUG_FINANZIERUNG_TEXTE = {
  [FahrzeugFinanzierung.GELEAST]: 'Geleast',
  [FahrzeugFinanzierung.FINANZIERT]: 'Finanziert',
  [FahrzeugFinanzierung.KEINE_FINANZIERUNG]: 'Nein'
};

type Props = {
  readonly vorgang: Vorgang;
  readonly aktualisiereVorgang: (vorgang: Partial<Vorgang>) => void;
  readonly speichereVorgang: (aenderungen?: Partial<Vorgang>) => void;
  readonly isLoading: boolean;
  readonly setLoading: (value: boolean) => void;
};

export function Auftraggeber({ vorgang, aktualisiereVorgang, speichereVorgang, isLoading, setLoading }: Props): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { gruppenVonMandant } = useUser();
  const [dialog, setDialog] = useState({ anlegen: false, aendern: false });

  const fahrzeugfinanzierungtypen = Object.entries(FAHRZEUG_FINANZIERUNG_TEXTE).map(([value, label]) => ({ label, value }));

  const handleAktualisierePersonMitKundennummer = (person: Person) => {
    setLoading(true);

    aktualisierePersonMitKundennummer({
      ...person,
      mandant: vorgang.mandant,
      weitereBearbeiter: Array.from(new Set([...(person.weitereBearbeiter ?? []), ...vorgang.ehemaligeBearbeiter, vorgang.userId]))
    })
      .then((gespeichertePerson) => {
        speichereVorgang({ auftraggeberId: gespeichertePerson.id });
        setDialog({ anlegen: false, aendern: false });
      })
      .catch((error) => enqueueSnackbar(getMessageFromError(error) ?? 'Auftraggeber konnte nicht gespeichert werden', ERROR_MESSAGE))
      .finally(() => setLoading(false));
  };

  const handleAuftraggeberAusgewaehlt = (person: Person) => {
    if (person.kundennummer) {
      speichereVorgang({ auftraggeberId: person.id });
    } else {
      handleAktualisierePersonMitKundennummer(person);
    }
  };

  const handleAuftraggeberAendernDialog = () => {
    setDialog((current) => ({ ...current, aendern: true }));
  };

  const handleAuftraggeberAnlegenDialog = () => {
    setDialog((current) => ({ ...current, anlegen: true }));
  };

  const handleAuftraggeberLoeschen = () => {
    speichereVorgang({
      auftraggeberId: null,
      auftraggeberVorsteuerabzugsberechtigt: null,
      auftraggeberRechtsschutzVorhanden: null,
      auftraggeberPostversand: null
    });
  };

  const isDisabled =
    !vorgang || isLoading || !isAenderbar(vorgang) || !aktionErlaubt(AKTION_KUNDENDATEN_ERFASSEN, gruppenVonMandant(vorgang?.mandant || ''), vorgang?.status);

  return (
    <Formular ueberschrift="Auftraggeber">
      <Grid container spacing={SPACING_BETWEEN_COLUMNS}>
        <Grid
          size={12}
          container
          sx={(theme) => ({
            [theme.breakpoints.down('md')]: {
              marginBottom: -theme.spacing(SPACING_BETWEEN_FORM_FIELDS)
            }
          })}
        >
          <Grid container spacing={SPACING_BETWEEN_FORM_FIELDS} size={12}>
            <Grid size={12}>
              <SearchPersonUnternehmen
                mandantId={vorgang?.mandant}
                isDisabled={isDisabled}
                label="Auftraggeber suchen"
                onSelect={(person) => handleAuftraggeberAusgewaehlt(person as Person)}
                testPraefix="auftraggeber"
                personenUnternehmenTyp={PersonenTyp.PERSON}
              />
            </Grid>
            <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                disabled={isDisabled || !vorgang?.auftraggeberId}
                onClick={() => handleAuftraggeberAendernDialog()}
                data-testid="auftraggeber-bearbeiten"
                size="large"
              >
                <Tooltip title="Ändern">
                  <EditIcon />
                </Tooltip>
              </IconButton>
              <IconButton disabled={isDisabled} onClick={() => handleAuftraggeberAnlegenDialog()} data-testid="auftraggeber-anlegen" size="large">
                <Tooltip title="Anlegen">
                  <AddBoxIcon />
                </Tooltip>
              </IconButton>
              <IconButton
                disabled={isDisabled || !vorgang?.auftraggeberId}
                onClick={() => handleAuftraggeberLoeschen()}
                data-testid="auftraggeber-loeschen"
                size="large"
              >
                <Tooltip title="Zuordnung löschen">
                  <DeleteIcon />
                </Tooltip>
              </IconButton>
            </Grid>
            <Grid size={12}>
              <WarningSign
                show={vorgang?.auftraggeber === null && vorgang?.auftraggeberId !== null}
                text="Der Auftraggeber wurde gelöscht oder Sie haben nicht die erforderlichen Rechte, diesen zu sehen."
              />
              {vorgang?.auftraggeber && <PersonAnzeige person={vorgang.auftraggeber} testPraefix="auftraggeber" />}
            </Grid>

            {vorgang?.auftraggeber && (
              <>
                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={vorgang.auftraggeberPostversand ?? false}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          aktualisiereVorgang({ auftraggeberPostversand: event.target.checked });
                        }}
                        name="Angebotsanfrage"
                        color="primary"
                      />
                    }
                    label="Postversand"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={vorgang.auftraggeberVip ?? false}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          aktualisiereVorgang({ auftraggeberVip: event.target.checked });
                        }}
                        name="VIP"
                        color="primary"
                      />
                    }
                    label="VIP"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <YesNoFormControl
                    labelText="Rechtsschutz"
                    value={vorgang.auftraggeberRechtsschutzVorhanden}
                    disabled={isDisabled}
                    onChange={(jaNein) => {
                      aktualisiereVorgang({ auftraggeberRechtsschutzVorhanden: jaNein });
                    }}
                    dataTestid="rechtsschutzVorhanden"
                    fullWidth
                  />
                </Grid>
                <Grid size={12}>
                  <YesNoFormControl
                    labelText={
                      <>
                        Fahrzeug im Betriebsvermögen <Typography variant="caption">(vorsteuerabzugsberechtigt)</Typography>{' '}
                      </>
                    }
                    value={vorgang?.auftraggeberVorsteuerabzugsberechtigt}
                    disabled={isDisabled}
                    onChange={(jaNein) => {
                      aktualisiereVorgang({ auftraggeberVorsteuerabzugsberechtigt: jaNein });
                    }}
                    dataTestid="vorsteuerabzugsberechtigt"
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl variant="standard" fullWidth>
                    <InputLabel>Fahrzeug finanziert oder geleast</InputLabel>
                    <Select
                      variant="standard"
                      label="Fahrzeug finanziert oder geleast"
                      value={vorgang.fahrzeugFinanziertGeleast ?? ''}
                      disabled={isDisabled}
                      onChange={(event: SelectChangeEvent<string>) => {
                        aktualisiereVorgang({ fahrzeugFinanziertGeleast: event.target.value as FahrzeugFinanzierung });
                      }}
                      data-testid={`fahrzeugdaten-fahrzeug-finanziert-geleased`}
                    >
                      {fahrzeugfinanzierungtypen.map(({ value, label }) => (
                        <MenuItem key={value} value={value} data-testid={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {vorgang?.fahrzeugFinanziertGeleast === FahrzeugFinanzierung.FINANZIERT && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <YesNoFormControl
                      labelText="Herstellernahe Finanzierungsbank"
                      value={vorgang?.herstellernahneFinanzierungsbank}
                      disabled={isDisabled}
                      onChange={(jaNein) => {
                        aktualisiereVorgang({ herstellernahneFinanzierungsbank: jaNein });
                      }}
                      dataTestid="FahrzeugFinanziert"
                      fullWidth
                    />
                  </Grid>
                )}
              </>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <CustomDatePicker
                openTo="year"
                views={['year', 'month', 'day']}
                label="Auftragsdatum"
                value={vorgang.auftragsdatum ?? vorgang.besichtigungen?.items?.[0]?.besichtigungstermin}
                disabled={isDisabled}
                onDateSelect={(datum) => aktualisiereVorgang({ auftragsdatum: datum })}
                data-testid="auftragsdatum"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {(dialog.anlegen || dialog.aendern) && (
        <PersonEingabeMaske
          personDatensatz={dialog.aendern ? vorgang?.auftraggeber : null}
          typ={PersonenTyp.PERSON}
          onClose={() => {
            setDialog({ anlegen: false, aendern: false });
          }}
          aktualisierePerson={handleAktualisierePersonMitKundennummer}
          isDisabled={isDisabled}
          mandant={vorgang.mandant}
          testPraefix="auftraggeber"
          zeigeKundennummer={true}
        />
      )}
    </Formular>
  );
}
